import { AssessmentQuestion, BaseUserProfile, Gender, MetricToImperialConverter, UnitPref } from 'fitbeat.models';
import moment from 'moment';
import React from 'react';
import { translate } from 'react-i18next';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { Button, ButtonTypes } from '../../components';
import Theme from '../../theme';
import { DatePicker, HeightField, RadioButtonGroup } from '../../ui';
import {
    heightChecksImperial, isDateOlderThan16, isNumberRequired, isValidDate, maxHeight,
    maxHeightImperial, minHeight, minHeightImperial, normalizeDateDDMMYYYY,
} from '../../utils';
import { defaultScale } from '../../utils/scaling';
import { DATE_OF_BIRTH, HEIGHT, WHATS_YOUR_GENDER } from '../assessmentQuestionnair';

interface IProps {
  metrics: any;
  questionsAndAnswers: AssessmentQuestion[];
  currentQuestion: AssessmentQuestion;
  disableNext: string;
  answers: AssessmentQuestion[];
  handleSubmit: any;
  isSaving: boolean;
  isUnderage: boolean;
  t: (key: string, context: any) => string;
  initialize(userProfile: any): void;
  onAnswerChange(answer: string): void;
  onPrevious(): void;
  onNext(answer: string): void;
}

class BodyscaleAssessmentQuestionsComponent extends React.PureComponent<IProps> {
  constructor(props: IProps) {
    super(props);
    const { initialize, metrics } = props;

    initialize({
      dateOfBirth: metrics && metrics.dateOfBirth,
      height: metrics && metrics.height,
    });
  }

  public onDateChange = (dateOfBirthString: string) => {
    const { onAnswerChange } = this.props;
    const dateOfBirth = moment(dateOfBirthString, 'DD-MM-YYYY').toDate();
    onAnswerChange(dateOfBirth.toString());
  }

  public render() {
    const {
      t,
      onAnswerChange,
      onPrevious,
      onNext,
      questionsAndAnswers,
      metrics,
      currentQuestion,
      answers,
      isSaving,
      isUnderage,
    } = this.props;

    const buildOption = (option: AssessmentQuestion) => {
      const genderQuestion = questionsAndAnswers.find((x) => x.questionId === WHATS_YOUR_GENDER);
      const gender = genderQuestion ? genderQuestion.answer : metrics.gender;
      const answerText = t(option.answer, {
        context: gender === Gender.Male ? Gender.Male : Gender.Female,
      });
      return {
        name: answerText,
        style: styles.optionName,
        isSelected: currentQuestion.answer === option.answer,
        setOption: () => onAnswerChange(option.answer),
      };
    };

    const getQuestion = (currentQuestion: AssessmentQuestion) => {
      const genderQuestion = questionsAndAnswers.find((x) => x.questionId === WHATS_YOUR_GENDER);
      const gender = genderQuestion ? genderQuestion.answer : metrics.gender;
      return t(currentQuestion.questionDescription, {
        context: gender === Gender.Male ? Gender.Male : Gender.Female,
      });
    }

    const questionComponent = () => {
      const isDobQuestion = answers.find((x) => x.answer === DATE_OF_BIRTH);
      const isHeightQuestion = answers.find((x) => x.answer === HEIGHT);

      if (isDobQuestion) {
        return <Field
                    forwardRef
                    confirmText='Confirm'
                    disableMinAgeRestriction
                    name='dateOfBirth'
                    showLabel={false}
                    onChange={this.onDateChange}
                    component={DatePicker}
                    normalize={normalizeDateDDMMYYYY}
                    validate={[isValidDate, isDateOlderThan16]}
                    placeholder='Date of birth'
                    innerContainerStyle={styles.inputContainer}
                    inputStyle={styles.inputStyle}
                    fontSize={inputFontSize}
                    labelStyle={styles.inputLabel}
                    errorStyle={styles.inputError}
                />;
      } else if (isHeightQuestion) {
        const {max, min, useImperialUnit} = this.getHeightPropertiesForUnitPrefAnswer(questionsAndAnswers);
        return <HeightField
            innerContainerStyle={styles.inputContainer}
            useImperialUnit={useImperialUnit}
            inputStyle={styles.inputStyle}
            fontSize={inputFontSize}
            labelStyle={styles.inputLabel}
            errorStyle={styles.inputError}
            inputHeight={inputHeight}
            onChange={(e: string) => {
                let height;
                if (useImperialUnit) {
                    const parsedHeightObj = JSON.parse(e);
                    height = MetricToImperialConverter.convertInchesAndFeetToCentimeters(parsedHeightObj)
                } else {
                    height = parseFloat(e);
                }
                !isNumberRequired(height) && !max(height) && !min(height) ||
                (useImperialUnit && !heightChecksImperial(e) ) ?
                    onAnswerChange(height.toString()) : onAnswerChange('');
            }}
        />;
      } else {
        return <Field
        forwardRef
        name='answeroption'
        component={RadioButtonGroup}
        inactiveStyle={styles.radioStyle}
        options={[
            ...answers.map(buildOption),
        ]}
    />;
      }
    };

    return (
      <View style={styles.container}>
        <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
          <View style={styles.addMargin}>
            <Text style={styles.questionText}>
              {getQuestion(currentQuestion)}
            </Text>
            <View>{questionComponent()}</View>
          </View>
        </ScrollView>
        <View style={styles.buttonContainer}>
          <>
            {!currentQuestion.isFirstQuestion && (
              <Button
                title={'Back'}
                onPress={onPrevious}
                fontColor={Theme.PRIMARY}
                buttonType={ButtonTypes.OUTLINE}
                containerStyle={styles.backButton}
              />
            )}
            <Button
              fontColor={Theme.WHITE}
              disabled={!currentQuestion.answer || isUnderage}
              isLoading={isSaving}
              onPress={onNext}
              title={currentQuestion.isLastQuestion ? 'Done' : 'Next'}
              containerStyle={styles.nextButton}
            />
          </>
        </View>
      </View>
    );
  }
  private getHeightPropertiesForUnitPrefAnswer(answers) {
    const { metrics } = this.props;
    if (metrics.useImperialUnit) {
      return {
        max: maxHeightImperial,
        min: minHeightImperial,
        useImperialUnit: true,
      };
    }
    return {
      min: minHeight,
      max: maxHeight,
      useImperialUnit: false,
    };
  }
}

const inputFontSize = defaultScale(16);
const inputHeight = defaultScale(46);
const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  addMargin: {
    marginTop: defaultScale(35),
    marginHorizontal: defaultScale(10),
  },
  questionText: {
    ...Theme.textStyle.heading2,
    color: Theme.BLACK,
    textAlign: 'center',
    marginBottom: defaultScale(35),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginVertical: defaultScale(15),
  },
  backButton: {
    flex: 0.3,
    marginRight: defaultScale(5),
    borderRadius: defaultScale(4),
  },
  nextButton: {
    flex: 1,
    borderRadius: defaultScale(4),
  },
  radioStyle: {
    height: defaultScale(25),
    width: defaultScale(25),
    borderRadius: defaultScale(25 / 2),
  },
  optionName: {
    ...Theme.textStyle.bodyCopyReg,
    marginLeft: defaultScale(12),
  },
  inputContainer: {
    borderRadius: defaultScale(3),
  },
  inputStyle: {
    width: '80%',
    height: inputHeight,
    marginLeft: defaultScale(5),
  },
  inputLabel: {
    fontSize: inputFontSize,
  },
  inputError: {
    fontSize: defaultScale(11),
  },
});

const selector = formValueSelector('bodyscaleAssessmentQuestions');
const mapStateToProps = (state) => {
  const dateOfBirthValue = selector(state, 'dateOfBirth');
  const dateOfBirth = moment(dateOfBirthValue, 'DD-MM-YYYY').toDate();
  const age = BaseUserProfile.getAge(dateOfBirth);
  const isUnderage = age < 16 ? true : false;

  return {
    isUnderage,
  };
};

const BodyscaleAssessmentQuestionsRedux = connect(mapStateToProps)(BodyscaleAssessmentQuestionsComponent);
export const BodyscaleAssessmentQuestions = reduxForm({ form: 'bodyscaleAssessmentQuestions' })(
    translate(['bodyscan'], { wait: true })(BodyscaleAssessmentQuestionsRedux));
